/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import * as R from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, FormFeedback, FormGroup, FormText, Input, Label, Table } from 'reactstrap';
import { withTitle } from '../../../reactutils/withTitle';
import { Album } from '../../../services/playlist';
import { Section, SectionHeading } from '../../common/Section';
import { AlbumImage } from '../../Playlist/AlbumImage';
import { usePathSegment } from '../../SysAdminApp/CacheStatisticsComponent';
import { AlbumLink } from '../AlbumLink';
import { loadSong, saveSong, songLoadAlbums } from './actions';
import { songEditAlbumsSelector, songEditSongSelector, songFullNameSelector } from './selectors';

const SongEdit: React.FC<{}> = () => {
    const [name, setName] = React.useState('');
    const [artist, setArtist] = React.useState('');
    const [remark, setRemark] = React.useState('');
    const [editMode, setEditMode] = React.useState(false);

    const song = useSelector(songEditSongSelector);
    const albums = useSelector(songEditAlbumsSelector);

    const [songId] = usePathSegment(3);

    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(loadSong(songId));
        dispatch(songLoadAlbums(songId));
    }, [dispatch, songId]);

    React.useEffect(() => {
        if (song) {
            setName(song.name || '');
            setArtist(song.artist || '');
            setRemark(song.remark || '');
        }
    }, [song]);

    const onSave = () => {
        dispatch(saveSong({ artist, id: songId, name, remark, audioUrl: '' }));
        setEditMode(false);
    };

    const onEdit = () => {
        setEditMode(true);
    };

    const onCancel = () => {
        setEditMode(false);
        dispatch(loadSong(songId));
    };

    const renderAlbum = (alb: Album) => {
        return (
            <tr key={alb.id}>
                <td><AlbumLink albumId={alb.id}>{alb.name}</AlbumLink></td>
                <td><AlbumImage imageId={alb.imageId} /></td>
            </tr>
        )
    }

    return (
        <>
            <Section>
                <SectionHeading>Bearbeiten</SectionHeading>
                <form>
                    <FormGroup>
                        <Label>Titel:</Label>
                        <Input
                            type="text"
                            name="name"
                            value={name}
                            onChange={e => setName(e.currentTarget.value)}
                            readOnly={!editMode}
                        />
                        <FormFeedback />
                        <FormText>Song Titel, max. 60 Zeichen</FormText>
                    </FormGroup>

                    <FormGroup>
                        <Label>Interpret:</Label>
                        <Input
                            type="text"
                            name="artist"
                            value={artist}
                            onChange={e => setArtist(e.currentTarget.value)}
                            readOnly={!editMode}
                        />
                        <FormFeedback />
                        <FormText>Name des Interpreten, max. 60 Zeichen</FormText>
                    </FormGroup>

                    <FormGroup>
                        <Label>Bemerkung:</Label>
                        <Input
                            componentClass="textarea"
                            type="textarea"
                            cols={80}
                            rows={5}
                            name="remark"
                            value={remark}
                            onChange={e => setRemark(e.currentTarget.value)}
                            readOnly={!editMode}
                        />
                        <FormFeedback />
                        <FormText>Bemerkungen zum Song</FormText>
                    </FormGroup>

                    {editMode && (
                        <>
                            <Button onClick={onSave} color="success">
                                Speichern
                            </Button>
                            <Button onClick={onCancel} color="secondary" className="ms-2">
                                Abbrechen
                            </Button>
                        </>
                    )}
                    {!editMode && (
                        <Button onClick={onEdit} color="warning">
                            Bearbeiten
                        </Button>
                    )}
                </form>
            </Section>
            <Section>
                <SectionHeading>Alben auf denen der Song zu finden ist ...</SectionHeading>
                <Table striped={true} >
                    <tbody>
                        {albums && albums.map(alb => renderAlbum(alb))}
                    </tbody>
                </Table>
            </Section>
        </>
    );
};

export default R.compose(withTitle('Song', songFullNameSelector))(SongEdit);
